import React, { useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import Button from "../../../../atoms/Button";
import InputField from "../../../../atoms/InputField";
import {
    UPDATE_ANNUAL_STATEMENT_DOCUMENTS,
    UPDATE_COMPILANCE_OPINIONS_DOCUMENTS,
    UPDATE_FINANCIAL_REPORTING,
    UPDATE_LEGAL_ENTITIE_DOCUMENTS,
    UPDATE_MAILBOX,
    UPDATE_NATURAL_PERSON_DOCUMENTS,
    UPDATE_PROVISIONAL_PAYMENTS_DOCUMENTS,
} from "../../../../../api/apiEmpresas";
import { UPLOAD_FILE } from "../../../../../api/apiFiles";
import { useSession } from "../../../../../context/SessionContext";

const ModalUploadDocument = ({ empresa, tipo, documentos, tabla, onClose, refetch, types }) => {
    const { user } = useSession();
    const [files, setFiles] = useState({});
    const [loading, setLoading] = useState(false);

    const currentType = types.find((t) => t.tableName === tipo);
    const rules = currentType?.rules || [];

    const updateHandlers = {
        archivo_permanente: {
            LegalEntitie: UPDATE_LEGAL_ENTITIE_DOCUMENTS,
            NaturalPerson: UPDATE_NATURAL_PERSON_DOCUMENTS,
        },
        opiniones_cumplimiento: UPDATE_COMPILANCE_OPINIONS_DOCUMENTS,
        declaracion_anual: UPDATE_ANNUAL_STATEMENT_DOCUMENTS,
        pagos_provicionales: UPDATE_PROVISIONAL_PAYMENTS_DOCUMENTS,
        reportes_financieros: UPDATE_FINANCIAL_REPORTING,
        buzones: UPDATE_MAILBOX,
    };

    const onUploadSubmit = async (values) => {
        try {
            setLoading(true);
            const uploadedFiles = [];
            const randomKey = crypto.randomUUID();

            for (const [uniqueKey, fileArray] of Object.entries(files)) {
                const [typeFile, idxStr] = uniqueKey.split("||");
                const rule = rules[Number(idxStr)];
                const title = `${rule.fileName}-${values.fecha}`;

                for (const file of fileArray) {
                    const { name: filename, type: mimetype } = file;
                    const { url, s3Key } = await UPLOAD_FILE(empresa.id, filename, mimetype, `${tabla}/${tipo}/`);

                    await fetch(url, {
                        method: "PUT",
                        headers: { "Content-Type": mimetype },
                        body: file,
                    });

                    uploadedFiles.push({
                        s3Key,
                        filename,
                        typeFile,
                        user: user?.roleInfo?.name,
                        primaryKey: randomKey,
                        uniqueFile: crypto.randomUUID(),
                        activo: true,
                        title,
                    });
                }
            }

            if (uploadedFiles.length === 0) {
                toast.error("No se han seleccionado archivos para subir.");
                return;
            }

            const previusDocs = documentos[tipo] || [];
            const uploadedTypes = [...new Set(uploadedFiles.map((file) => file.typeFile))].join(", ");

            previusDocs.push({
                key: randomKey,
                fecha: values.fecha,
                archivos: uploadedFiles,
                types: uploadedTypes,
                activo: true,
            });

            const updatePayload = { [tipo]: previusDocs };

            const handler =
                tabla === "archivo_permanente"
                    ? updateHandlers[tabla][empresa.type]
                    : updateHandlers[tabla];

            if (handler) await handler(documentos.id, updatePayload);

            refetch();
            onClose();
            toast.success("Documentos subidos correctamente");
        } catch (error) {
            console.error(error);
            toast.error("Error al subir archivos");
        } finally {
            setLoading(false);
        }
    };

    const initialValues = { fecha: "" };

    const validate = (values) => {
        const errors = {};
        if (!values.fecha) {
            errors.fecha = "Campo requerido";
        }

        rules.forEach((rule, idx) => {
            const uniqueKey = `${rule.typeFile}||${idx}`;
            if (rule?.required && (!files[uniqueKey] || files[uniqueKey].length === 0)) {
                errors[uniqueKey] = "Archivo requerido";
            }
        });

        return errors;
    };

    const handleFileChange = (e, uniqueKey) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prev) => ({
            ...prev,
            [uniqueKey]: [...(prev[uniqueKey] || []), ...selectedFiles],
        }));
    };

    return (
        <Formik onSubmit={onUploadSubmit} initialValues={initialValues} validate={validate}>
            {({ errors }) => (
                <Form className="p-4 flex flex-col items-center space-y-6">
                    <InputField label="Fecha del documento" name="fecha" type="date" />
                    {rules.map((rule, idx) => {
                        const uniqueKey = `${rule.typeFile}||${idx}`;
                        return (
                            <div key={uniqueKey} className="w-full flex flex-col md:flex-row md:space-x-4 border rounded-md p-4">
                                <div className="w-full flex flex-col">
                                    <label className="text-sm font-bold mb-1">
                                        {rule.name} {rule?.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="file"
                                        multiple
                                        accept={`.${rule.typeFile}`}
                                        onChange={(e) => handleFileChange(e, uniqueKey)}
                                        className="w-full p-2 border-2 border-black rounded-xl"
                                    />
                                    {errors[uniqueKey] && (
                                        <p className="text-[#e73842] text-sm mb-2">
                                            {errors[uniqueKey]}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <Button
                        type="submit"
                        text={loading ? <PropagateLoader color="red" /> : "Enviar"}
                        bgColor="blue"
                        disabled={loading}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default ModalUploadDocument;
